import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaUserTie, FaCheckCircle, FaClock, FaTags, FaAward, FaHeadset, FaStar } from 'react-icons/fa';
import StatsSection from '../components/StatsSection';
import PopupContactForm from '../components/PopupContactForm';
import './Home.css';

const Home = () => {
  const services = [
    {
      id: 0,
      title: 'Layout Design',
      slug: 'layout-design',
      shortDescription: 'Expert DTCP approved layout design services in Coimbatore. Master planning and site layout design for residential and commercial real estate developments with optimal plot divisions and infrastructure planning.',
      image: '/image/home/service/expert_dtcp_approved_layout_design_services_in_coimbatore.webp'
    },
    {
      id: 1,
      title: 'Building Construction',
      slug: 'construction',
      shortDescription: 'Professional building construction services in Coimbatore. We specialize in high-quality residential and commercial construction, delivering projects on time with superior craftsmanship.',
      image: '/image/home/service/professional_building_construction_services_in_coimbatore.webp'
    },
    {
      id: 2,
      title: 'Land Sale & Purchase',
      slug: 'land-sale',
      shortDescription: 'Trusted land sale and purchase services in Coimbatore. From finding DTCP approved plots to complete legal verification and documentation, we ensure secure property transactions.',
      image: '/image/home/service/trusted_land_sale_and_purchase_services_in_coimbatore.webp'
    },
    {
      id: 3,
      title: 'Documentation Services',
      slug: 'documentation',
      shortDescription: 'Complete property documentation services in Coimbatore. Our legal experts handle Patta transfer, registration, DTCP approvals, and all property-related permits and clearances.',
      image: '/image/home/service/complete_property_documentation_services_in_coimbatore.webp'
    },
    {
      id: 4,
      title: 'Real Estate Services',
      slug: 'real-estate',
      shortDescription: 'Comprehensive real estate services in Coimbatore. Expert consultation for buying, selling, and investing in residential and commercial properties across Coimbatore and surrounding areas.',
      image: '/image/home/service/comprehensive_real_estate_services_in_coimbatore.webp'
    },
    {
      id: 5,
      title: 'Renovation & Remodeling',
      slug: 'renovation',
      shortDescription: 'Professional renovation and remodeling services in Coimbatore. Transform your existing property with modern designs, quality materials, and expert craftsmanship.',
      image: '/image/home/service/professional_renovation_and_remodeling_services_in_coimbatore.webp'
    },
    {
      id: 6,
      title: 'Interior & Exterior Design',
      slug: 'interior-design',
      shortDescription: 'Creative interior and exterior design services in Coimbatore. Customized designs that blend aesthetics with functionality for residential and commercial spaces.',
      image: '/image/home/service/creative_interior_and_exterior_design_services_in_coimbatore.webp'
    },
    {
      id: 7,
      title: 'Project Management',
      slug: 'project-management',
      shortDescription: 'End-to-end project management services in Coimbatore. From planning to execution, we ensure timely delivery and quality control for all construction projects.',
      image: '/image/home/service/end_to_end_project_management_services_in_coimbatore.webp'
    }
  ];

  const projects = [
    { id: 1, name: 'Luxury Villa Project', location: 'Downtown Area', type: 'Residential', image: '/image/home/featured_projects/luxury_villa_project_downtown_area_residential.webp' },
    { id: 2, name: 'Commercial Complex', location: 'Business District', type: 'Commercial', image: '/image/home/featured_projects/commercial_complex.webp' },
    { id: 3, name: 'Apartment Building', location: 'Suburban Area', type: 'Residential', image: '/image/home/featured_projects/apartment_building.webp' }
  ];

  const testimonials = [
    { 
      id: 1, 
      clientName: 'Rajendran M', 
      feedback: 'Excellent service and quality construction in Saravanampatti. Prime Crown Assets completed our dream home on time and within budget. Highly recommend for construction in Coimbatore!', 
      rating: 5,
      location: 'Saravanampatti, Coimbatore'
    },
    { 
      id: 2, 
      clientName: 'Lakshmi Priya', 
      feedback: 'Professional team with great attention to detail. The DTCP layout documentation process was smooth and hassle-free. Best real estate company in Coimbatore!', 
      rating: 5,
      location: 'Vadavalli, Coimbatore'
    },
    { 
      id: 3, 
      clientName: 'Senthil Kumar', 
      feedback: 'Best construction company in Coimbatore. Transparent pricing and quality work. Built our commercial building in Gandhipuram. Very satisfied with their service!', 
      rating: 5,
      location: 'Gandhipuram, Coimbatore'
    }
  ];

  const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [servicesRef, servicesInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [featuresRef, featuresInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [projectsRef, projectsInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [testimonialsRef, testimonialsInView] = useInView({ triggerOnce: true, threshold: 0.1 });

  // Animation Variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const textReveal = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  return (
    <div className="home">
      <Helmet>
        <title>Prime Crown Assets - Real Estate & Construction Services in Coimbatore</title>
        <meta name="description" content="Leading real estate and construction company in Coimbatore, Tamil Nadu. Expert services in layout design, building construction, land sale, DTCP approved layouts, and property documentation." />
        <meta name="keywords" content="real estate services Coimbatore, construction company Coimbatore, layout design Coimbatore, land sale Coimbatore, DTCP approved layout Coimbatore, building construction Coimbatore, property documentation Coimbatore" />
        <meta property="og:title" content="Prime Crown Assets - Real Estate & Construction Services in Coimbatore" />
        <meta property="og:description" content="Leading real estate and construction company in Coimbatore offering layout design, building construction, land sale, and property documentation services." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://primecrownassets.com/" />
        <meta property="og:image" content="https://primecrownassets.com/logo.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Prime Crown Assets - Real Estate & Construction Services in Coimbatore" />
        <meta name="twitter:description" content="Leading real estate and construction company in Coimbatore offering comprehensive construction and real estate solutions." />
        <link rel="canonical" href="https://primecrownassets.com/" />
        <meta name="geo.region" content="IN-TN" />
        <meta name="geo.placename" content="Coimbatore" />
        <meta name="geo.position" content="11.0168;76.9558" />
        <meta name="ICBM" content="11.0168, 76.9558" />
      </Helmet>
      <section 
        className="hero" 
        ref={heroRef}
      >
        {/* LCP image as actual img tag for faster browser discovery */}
        <img
          src="/image/home/banner/premier_real_estate_construction_services_in_coimbatore.webp"
          alt="Prime Crown Assets - Real Estate & Construction Services in Coimbatore"
          className="hero-bg-image"
          fetchpriority="high"
          decoding="sync"
        />
        <div className="hero-overlay"></div>
        <motion.div
          className="hero-content"
          initial="hidden"
          animate={heroInView ? "visible" : "hidden"}
          variants={staggerContainer}
        >
          <motion.h1 variants={textReveal}>
            Premier Real Estate & Construction Services in Coimbatore
          </motion.h1>
          <motion.p variants={textReveal}>
            From DTCP Approved Layouts to Building Construction and Property Documentation — Your Trusted Partner for Real Estate Solutions in Coimbatore, Tamil Nadu.
          </motion.p>
          <motion.div className="hero-buttons" variants={fadeInUp}>
            <Link to="/services" className="btn btn-primary">Our Services</Link>
            <Link to="/contact" className="btn btn-outline">Contact Us</Link>
          </motion.div>
        </motion.div>

        <div className="scroll-indicator">
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            ↓
          </motion.div>
        </div>
      </section>

      <section className="section services-overview" ref={servicesRef}>
        <div className="container">
          <motion.div
            className="section-header"
            initial="hidden"
            animate={servicesInView ? "visible" : "hidden"}
            variants={textReveal}
          >
            <h2 className="section-title">Our <span>Services</span></h2>
            <p className="section-subtitle">Comprehensive solutions tailored to your unique needs.</p>
          </motion.div>

          <motion.div
            className="services-grid"
            initial="hidden"
            animate={servicesInView ? "visible" : "hidden"}
            variants={staggerContainer}
          >
            {services.map((service) => (
              <motion.div
                key={service.id}
                className="service-card"
                style={{ backgroundImage: `url(${service.image})` }}
                variants={fadeInUp}
              >
                <div className="service-overlay">
                  <h3>{service.title}</h3>
                  <p>{service.shortDescription}</p>
                  <Link to={`/services/${service.slug}`} className="btn-text">Learn More →</Link>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <StatsSection />

      <section className="section why-choose" ref={featuresRef}>
        <div className="container">
          <motion.div
            className="section-header"
            initial="hidden"
            animate={featuresInView ? "visible" : "hidden"}
            variants={textReveal}
          >
            <h2 className="section-title">Why <span>Choose Us</span></h2>
            <p className="section-subtitle">Excellence in every project, commitment in every step.</p>
          </motion.div>

          <motion.div
            className="features-grid"
            initial="hidden"
            animate={featuresInView ? "visible" : "hidden"}
            variants={staggerContainer}
          >
            {[
              { icon: <FaUserTie />, title: 'Experienced Professionals', desc: 'Team of skilled engineers and architects with 15+ years of experience.' },
              { icon: <FaCheckCircle />, title: 'Legal & Verified', desc: 'All documentation legally verified and compliant with government regulations.' },
              { icon: <FaClock />, title: 'On-Time Delivery', desc: 'Projects completed on schedule with strict timeline management.' },
              { icon: <FaTags />, title: 'Transparent Pricing', desc: 'No hidden costs. Clear quotations and detailed cost breakdowns.' },
              { icon: <FaAward />, title: 'Quality Assurance', desc: 'Premium materials and rigorous quality checks at every phase.' },
              { icon: <FaHeadset />, title: 'Customer Support', desc: '24/7 customer support and post-construction maintenance.' }
            ].map((feature, index) => (
              <motion.div
                key={index}
                className="feature-card"
                variants={fadeInUp}
              >
                <div className="feature-icon-wrapper">
                  {feature.icon}
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="section featured-projects" ref={projectsRef}>
        <div className="container">
          <motion.div
            className="section-header"
            initial="hidden"
            animate={projectsInView ? "visible" : "hidden"}
            variants={textReveal}
          >
            <h2 className="section-title">Featured <span>Projects</span></h2>
            <p className="section-subtitle">A glimpse into our portfolio of excellence.</p>
          </motion.div>

          <motion.div
            className="projects-grid"
            initial="hidden"
            animate={projectsInView ? "visible" : "hidden"}
            variants={staggerContainer}
          >
            {projects.map((project) => (
              <motion.div
                key={project.id}
                className="project-card"
                variants={fadeInUp}
              >
                <div className="project-image" style={{ backgroundImage: `url(${project.image})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
                  <div className="project-overlay">
                    <h3>{project.name}</h3>
                    <p>{project.location} | {project.type}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="section testimonials-preview" ref={testimonialsRef}>
        <div className="container">
          <motion.div
            className="section-header"
            initial="hidden"
            animate={testimonialsInView ? "visible" : "hidden"}
            variants={textReveal}
          >
            <h2 className="section-title">Client <span>Testimonials</span></h2>
          </motion.div>

          <motion.div
            className="testimonials-grid"
            initial="hidden"
            animate={testimonialsInView ? "visible" : "hidden"}
            variants={staggerContainer}
          >
            {testimonials.map((testimonial) => (
              <motion.div
                key={testimonial.id}
                className="testimonial-card"
                variants={fadeInUp}
              >
                <div className="quote-mark">"</div>
                <p>{testimonial.feedback}</p>
                <h4>- {testimonial.clientName}</h4>
                <div className="rating">
                  {[...Array(testimonial.rating)].map((_, i) => <FaStar key={i} />)}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="cta">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
          >
            <h2>Ready to Build Your Dream?</h2>
            <p>Contact us today for a free consultation and quote.</p>
            <Link to="/contact" className="btn btn-primary">Get Started</Link>
          </motion.div>
        </div>
      </section>

      {/* Popup Contact Form */}
      <PopupContactForm />
    </div>
  );
};

export default Home;
